import { useState, useEffect, useRef } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { getApiUrl } from '../config/api'
import '../App.css'

function ContentDisplay() {
  const { pageSlug, contentSlug } = useParams()
  const location = useLocation()
  const [content, setContent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const contentRef = useRef(null)
  const fallbackContentId = location.state?.contentId

  useEffect(() => {
    if (!pageSlug || (!contentSlug && !fallbackContentId)) {
      setError('Page slug or content identifier is missing')
      setLoading(false)
      return
    }

    const controller = new AbortController()
    setError(null)

    const fetchContentData = async (identifier, useFallback = false) => {
      const apiUrl = getApiUrl(`pages/${pageSlug}/contents/${identifier}`)
      console.log('Fetching content detail from:', apiUrl)

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        signal: controller.signal,
      })

      if (!response.ok) {
        if (response.status === 404) {
          const attemptFallback = !useFallback && fallbackContentId && `${fallbackContentId}` !== `${identifier}`
          if (attemptFallback) {
            return fetchContentData(fallbackContentId, true)
          }
          throw new Error('Content not found')
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log('Content detail received:', data)

      if (!data.content) {
        throw new Error('Content not found for this page')
      }

      setContent(data.content)
      updateMetaTags(data.content, data.page)
    }

    const extractPlainTextFromHtml = (html = '') => {
      try {
        if (typeof window !== 'undefined' && 'DOMParser' in window) {
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')
          const text = (doc?.body?.textContent || doc?.textContent || '').replace(/\s+/g, ' ').trim()
          return text
        }
      } catch (_) {}
      const withoutStyle = html.replace(/<style[\s\S]*?<\/style>/gi, '')
      const withoutScript = withoutStyle.replace(/<script[\s\S]*?<\/script>/gi, '')
      const noTags = withoutScript.replace(/<[^>]*>/g, ' ')
      return noTags.replace(/\s+/g, ' ').trim()
    }

    const updateMetaTags = (contentData, pageData) => {
      const baseTitle = pageData?.meta_title || pageData?.title || 'SkillSiksha'
      const title = contentData?.title ? `${contentData.title} - SkillSiksha` : `${baseTitle} - SkillSiksha`
      document.title = title

      let description = ''
      if (contentData?.content) {
        const textContent = extractPlainTextFromHtml(contentData.content)

        description = textContent.length > 160 ? `${textContent.substring(0, 157)}...` : textContent
      } else if (pageData?.meta_description) {
        description = pageData.meta_description
      } else if (contentData?.title) {
        description = `Complete details about ${contentData.title}. Find eligibility, important dates, and application process at SkillSiksha.`
      } else {
        description = 'SkillSiksha - Your Gateway to Government Jobs & Career Success'
      }

      let metaDesc = document.querySelector('meta[name="description"]')
      if (!metaDesc) {
        metaDesc = document.createElement('meta')
        metaDesc.setAttribute('name', 'description')
        document.getElementsByTagName('head')[0].appendChild(metaDesc)
      }
      metaDesc.setAttribute('content', description)

      const keywords = []
      if (contentData?.title) {
        keywords.push(contentData.title)
      }
      if (pageData?.title) {
        keywords.push(pageData.title)
      }
      if (pageData?.meta_keywords) {
        pageData.meta_keywords
          .split(',')
          .map((keyword) => keyword.trim())
          .forEach((keyword) => keywords.push(keyword))
      }
      keywords.push('SkillSiksha', 'government jobs', 'sarkari naukri', 'government job preparation', 'competitive exams')

      const uniqueKeywords = Array.from(
        new Set(
          keywords
            .map((keyword) => keyword.trim())
            .filter((keyword) => keyword.length > 0)
        )
      )

      let metaKeywords = document.querySelector('meta[name="keywords"]')
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta')
        metaKeywords.setAttribute('name', 'keywords')
        document.getElementsByTagName('head')[0].appendChild(metaKeywords)
      }
      metaKeywords.setAttribute('content', uniqueKeywords.join(', '))
    }

    const fetchContent = async () => {
      setLoading(true)
      try {
        const identifier = contentSlug || fallbackContentId
        if (!identifier) {
          throw new Error('Content identifier is missing')
        }
        await fetchContentData(identifier)
      } catch (err) {
        if (err.name === 'AbortError') return
        console.error('Error fetching content detail:', err)
        setError(err.message || 'Failed to load content')
      } finally {
        setLoading(false)
      }
    }

    fetchContent()

    return () => {
      controller.abort()
    }
  }, [pageSlug, contentSlug, location.state])


  if (loading) {
    return (
      <div className="page-section" style={{ marginTop: '20px', paddingTop: '20px' }}>
        <div className="container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !content) {
    return (
      <div className="page-section" style={{ marginTop: '20px', paddingTop: '20px' }}>
        <div className="container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h1>Content Not Found</h1>
            <p>{error || 'The content you are looking for does not exist.'}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <section className="page-section content-display-section" style={{marginTop: '-50px'}}>
      <div className="container">
        <div className="page-content content-display-wrapper" style={{marginTop: '-10px'}}>
          {/* Content Title */}
          <h1 className="page-title content-display-title">
            {content.title}
          </h1>

          {/* Content HTML */}
          {content.content ? (
            <div 
              ref={contentRef}
              className="content-html-display content-display-body"
              dangerouslySetInnerHTML={{ __html: content.content }}
            />
          ) : (
            <div className="content-no-available">
              <p>No content available</p>
            </div>
          )}

          {/* Direct Link (if available) */}
          {content.link && content.link_type === 'direct' && (
            <div className="content-external-link">
              <a 
                href={content.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Visit External Link <i className="fas fa-external-link-alt"></i>
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default ContentDisplay

